import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Grid, List, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import WishlistCarCard from '../../components/ALLJsx/WishlistCarCard';
import { useToast } from '../../contexts/ToastContext';
import { useEnsureCars } from '../../hooks/useEnsureCars';
import '../AllCss/Wishlist.css';

function Wishlist() {
  useEnsureCars();
  const toast = useToast();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const { isAuthenticated } = useSelector((state) => state.auth);
  
  // Get wishlist car IDs from Redux
  const wishlistIds = useSelector((state) => state.wishlist.items);
  const allCars = useSelector((state) => state.cars.items);
  
  // Filter cars that are in the wishlist
  const wishlistCars = allCars.filter(car => wishlistIds.includes(car.id));
  
  // Pagination calculations
  const totalPages = Math.ceil(wishlistCars.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageCars = wishlistCars.slice(startIndex, endIndex);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShareWishlist = async () => {
    const shareData = {
      title: 'Saved Vehicle Collection',
      text: `View my collection of ${wishlistCars.length} saved vehicles`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Collection link copied to clipboard');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error sharing:', err);
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="wishlist-page">
        <Navbar />
        <div className="container">
          <div className="wishlist-empty">
            <h2>Authentication Required</h2>
            <p>Please sign in to access your saved vehicle collection</p>
            <Link to="/auth" className="btn btn-primary">
              Sign In to Account
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <Navbar />
      <div className="container">
        {/* Header */}
        <div className="wishlist-header">
          <h1 className="wishlist-title">Saved Vehicles</h1>
          
          <div className="wishlist-actions">
            {/* View Toggle */}
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <Grid size={18} />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <List size={18} />
              </button>
            </div>

            {/* Export Button */}
            <button className="export-btn" onClick={handleShareWishlist} aria-label="Share collection">
              <ExternalLink size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        {wishlistCars.length === 0 ? (
          <div className="wishlist-empty">
            <h2>No Saved Vehicles</h2>
            <p>Begin building your collection by saving vehicles of interest</p>
            <Link to="/inventory" className="btn btn-primary">
              View Available Inventory
            </Link>
          </div>
        ) : (
          <>
            <div className={`wishlist-content ${viewMode}`}>
              {/* Car Cards */}
              <div className={`wishlist-cars ${viewMode}-view`}>
                {currentPageCars.map((car) => (
                  <WishlistCarCard key={car.id} car={car} viewMode={viewMode} />
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                <div className="pagination-numbers">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    
                    // Always show first 3 pages (1, 2, 3)
                    if (pageNumber <= 3) {
                      return (
                        <button
                          key={pageNumber}
                          className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                    
                    // Show ellipsis after page 3 if there are more pages before the last 2
                    if (pageNumber === 4 && totalPages > 5) {
                      return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                    }
                    
                    // Always show last 2 pages (n-1, n)
                    if (pageNumber >= totalPages - 1) {
                      return (
                        <button
                          key={pageNumber}
                          className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                    
                    return null;
                  })}
                </div>
                
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default Wishlist;